import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  TextField,
  Typography,
  CircularProgress,
  Card,
  CardContent,
  Chip,
  Divider,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { toast } from "react-toastify";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import HotelReservaModal from "../HotelReservaModal";

const useStyles = makeStyles((theme) => ({
  dialogContent: {
    minWidth: 500,
    [theme.breakpoints.down("sm")]: {
      minWidth: "unset",
    },
  },
  quartoCard: {
    marginBottom: theme.spacing(1),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
  },
  quartoHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(0.5),
  },
  precoDestaque: {
    fontWeight: "bold",
    color: theme.palette.primary.main,
    fontSize: "1.1rem",
  },
  chipTipo: {
    marginRight: theme.spacing(1),
  },
  periodo: {
    backgroundColor: theme.palette.action.hover,
    padding: theme.spacing(1),
    borderRadius: 4,
    marginBottom: theme.spacing(2),
  },
  reservarBtn: {
    marginTop: theme.spacing(1),
  },
  semResultados: {
    textAlign: "center",
    padding: theme.spacing(3),
    color: theme.palette.text.secondary,
  },
}));

const HotelDisponibilidadeModal = ({ open, onClose, contact }) => {
  const classes = useStyles();
  const today = new Date().toISOString().split("T")[0];
  const tomorrow = new Date(Date.now() + 86400000).toISOString().split("T")[0];

  const [entrada, setEntrada] = useState(today);
  const [saida, setSaida] = useState(tomorrow);
  const [loading, setLoading] = useState(false);
  const [resultado, setResultado] = useState(null);
  const [reservaModalOpen, setReservaModalOpen] = useState(false);
  const [quartoSelecionado, setQuartoSelecionado] = useState(null);

  const handleBuscar = async () => {
    if (!entrada || !saida) {
      toast.error("Informe as datas de entrada e saída.");
      return;
    }
    if (saida <= entrada) {
      toast.error("A data de saída deve ser posterior à entrada.");
      return;
    }
    setLoading(true);
    setResultado(null);
    try {
      const { data } = await api.get("/hotel-mineiro/disponibilidade", {
        params: { entrada, saida },
      });
      setResultado(data.data);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleReservar = (quarto) => {
    setQuartoSelecionado({ ...quarto, entrada, saida });
    setReservaModalOpen(true);
  };

  const handleClose = () => {
    setResultado(null);
    onClose();
  };

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>Consultar Disponibilidade — Hotel do Mineiro</DialogTitle>
        <DialogContent className={classes.dialogContent}>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField
                label="Check-in"
                type="date"
                value={entrada}
                onChange={(e) => setEntrada(e.target.value)}
                fullWidth
                InputLabelProps={{ shrink: true }}
                variant="outlined"
                size="small"
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Check-out"
                type="date"
                value={saida}
                onChange={(e) => setSaida(e.target.value)}
                fullWidth
                InputLabelProps={{ shrink: true }}
                variant="outlined"
                size="small"
              />
            </Grid>
          </Grid>

          {loading && (
            <div style={{ textAlign: "center", padding: 24 }}>
              <CircularProgress size={32} />
            </div>
          )}

          {resultado && (
            <div style={{ marginTop: 16 }}>
              <div className={classes.periodo}>
                <Typography variant="body2">
                  <strong>Período:</strong> {resultado.periodo.entrada} até{" "}
                  {resultado.periodo.saida} &nbsp;·&nbsp;{" "}
                  <strong>{resultado.periodo.noites} noite(s)</strong>
                </Typography>
                <Typography variant="body2">
                  <strong>Quartos disponíveis:</strong>{" "}
                  {resultado.total_disponiveis}
                </Typography>
              </div>

              <Divider style={{ marginBottom: 12 }} />

              {resultado.quartos.length === 0 ? (
                <div className={classes.semResultados}>
                  <Typography>Nenhum quarto disponível neste período.</Typography>
                </div>
              ) : (
                resultado.quartos.map((q) => (
                  <Card key={q.id} className={classes.quartoCard} elevation={0}>
                    <CardContent>
                      <div className={classes.quartoHeader}>
                        <div>
                          <Chip
                            label={q.tipo}
                            size="small"
                            color="primary"
                            className={classes.chipTipo}
                          />
                          <Typography
                            variant="subtitle2"
                            component="span"
                          >
                            Quarto {q.numero} · {q.capacidade} pessoa(s)
                          </Typography>
                        </div>
                        <div>
                          <Typography className={classes.precoDestaque}>
                            R$ {Number(q.total_estadia).toFixed(2)}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            R$ {Number(q.preco_noite).toFixed(2)}/noite
                          </Typography>
                        </div>
                      </div>
                      {q.descricao && (
                        <Typography variant="body2" color="textSecondary">
                          {q.descricao}
                        </Typography>
                      )}
                      <Button
                        size="small"
                        variant="contained"
                        color="primary"
                        className={classes.reservarBtn}
                        onClick={() => handleReservar(q)}
                      >
                        Reservar este quarto
                      </Button>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Fechar</Button>
          <Button
            onClick={handleBuscar}
            color="primary"
            variant="contained"
            disabled={loading}
          >
            {loading ? <CircularProgress size={18} /> : "Buscar"}
          </Button>
        </DialogActions>
      </Dialog>

      {reservaModalOpen && quartoSelecionado && (
        <HotelReservaModal
          open={reservaModalOpen}
          onClose={() => setReservaModalOpen(false)}
          quarto={quartoSelecionado}
          contact={contact}
          onReservaCriada={() => {
            setReservaModalOpen(false);
            handleClose();
          }}
        />
      )}
    </>
  );
};

export default HotelDisponibilidadeModal;
